import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "../NavBar/Nav";
import FooterNew from "../NavBar/FooterNew";
import { useExperienceCart } from "./ExperienceCartContext";
import { formatMoney, formatDisplayDate } from "./experienceUtils";
import "./Experiences.css";

const generateReference = () => {
  const stamp = Date.now().toString(36).toUpperCase().slice(-6);
  return `LX-${stamp}`;
};

export default function ExperienceCheckout() {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useExperienceCart();

  const [form, setForm] = useState({ fullName: "", email: "", phone: "", notes: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const currency = items.length ? items[0].currency : "USD";

  if (items.length === 0) {
    return (
      <div className="exp-scope">
        <Nav />
        <div className="exp-page-loading">
          <div style={{ textAlign: "center" }}>
            <p style={{ marginBottom: 16 }}>Your cart is empty, so there's nothing to check out yet.</p>
            <button className="exp-inline-btn" onClick={() => navigate("/experiences")}>
              Find experiences
            </button>
          </div>
        </div>
        <FooterNew />
      </div>
    );
  }

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Please enter the lead traveler's name.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      next.email = "Please enter a valid email address.";
    }
    if (!form.phone.trim() || form.phone.trim().length < 7) {
      next.phone = "Please enter a valid phone number.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    const reference = generateReference();
    const snapshot = { items, subtotal, currency, form, reference };

    // No live payment/booking API is defined for this flow yet, so we
    // record the request locally and hand the traveler a reference number.
    // Swapping in a real POST to the backend later only touches this block.
    setTimeout(() => {
      clearCart();
      setSubmitting(false);
      navigate("/experiences/confirmation", { state: snapshot });
    }, 600);
  };

  return (
    <div className="exp-scope">
      <div style={{ position: "relative" }}>
        <Nav />
      </div>

      <div className="exp-container">
        <div className="exp-page-heading">
          <h1>Checkout</h1>
          <p>Tell us who's traveling — we'll confirm each experience directly with you.</p>
        </div>

        <form className="exp-checkout-body" onSubmit={handleSubmit}>
          <div className="exp-form-card">
            <h3>Lead traveler details</h3>
            <p>We'll use these details to confirm your booking and send your vouchers.</p>

            <div className="exp-form-row">
              <div className="exp-form-group">
                <label>Full name</label>
                <input
                  type="text"
                  placeholder="As it appears on your ID"
                  value={form.fullName}
                  onChange={handleChange("fullName")}
                />
                {errors.fullName && <div className="exp-form-error">{errors.fullName}</div>}
              </div>
              <div className="exp-form-group">
                <label>Phone number</label>
                <input
                  type="tel"
                  placeholder="+1 555 123 4567"
                  value={form.phone}
                  onChange={handleChange("phone")}
                />
                {errors.phone && <div className="exp-form-error">{errors.phone}</div>}
              </div>
            </div>

            <div className="exp-form-group" style={{ marginBottom: 16 }}>
              <label>Email address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange("email")}
              />
              {errors.email && <div className="exp-form-error">{errors.email}</div>}
            </div>

            <div className="exp-form-group">
              <label>Notes for our team (optional)</label>
              <textarea
                rows={4}
                placeholder="Dietary needs, pickup location, accessibility requirements..."
                value={form.notes}
                onChange={handleChange("notes")}
              />
            </div>

            <button className="exp-btn-primary" type="submit" disabled={submitting}>
              {submitting ? "Confirming booking..." : "Confirm booking"}
            </button>
          </div>

          <div className="exp-summary-card">
            <h3>Order summary</h3>
            {items.map((item) => (
              <div className="exp-mini-line-item" key={item.lineId}>
                {item.image ? (
                  <img src={item.image} alt={item.title} />
                ) : (
                  <div
                    className="exp-product-image-placeholder"
                    style={{ position: "static", width: 56, height: 56, borderRadius: 8 }}
                  />
                )}
                <div>
                  <div className="exp-mini-line-title">{item.title}</div>
                  <div className="exp-mini-line-meta">
                    {item.date ? `${formatDisplayDate(item.date)} · ` : ""}
                    {item.travelers} traveler{item.travelers > 1 ? "s" : ""} ·{" "}
                    {formatMoney(item.price * item.travelers, item.currency)}
                  </div>
                </div>
              </div>
            ))}
            <div className="exp-summary-total">
              <span>Total due</span>
              <span>{formatMoney(subtotal, currency)}</span>
            </div>
          </div>
        </form>
      </div>

      <FooterNew />
    </div>
  );
}
